import { ChartConfig } from '@blisspointmedia/bpm-types/dist/dashboarding';
import {
  DashboardFilterSkipReason,
  DashboardFilterStatus,
  FilterNode,
  filterDepth,
  filterDimensions,
  isObjectNode,
  MAX_RAW_FILTER_DEPTH
} from '@blisspointmedia/bpm-types/dist/querying';
import { DimensionId } from '@blisspointmedia/bpm-types/dist/querying/ids';
import {
  filterDimensionLabel,
  formatFilterNode
} from '@Components/ChartRenderer/chart-context-line-utils';
import { DashboardFilterChip } from '@Components/ChartRenderer/chart-filter-line-constants';
import { ChartWithData } from '../Charts/hooks/useChartData';

// `{dimension}` is replaced with the display label of the dimension the skip is about.
export const SKIP_REASON_TEXT = {
  CARD_OPTED_OUT: 'This card ignores dashboard filters on {dimension}.',
  CHART_HAS_OWN_FILTER: 'This chart has its own {dimension} filter.',
  CHART_USES_FILTER_PRESET: 'This chart uses a saved filter preset.',
  MODEL_FILTERS_UNSUPPORTED: "This chart's data does not accept filters.",
  DIMENSION_NOT_IN_DATA: "This chart's data has no {dimension} dimension.",
  INVALID_FILTER: 'This filter is not valid.',
  INVALID_WITH_CHART: 'This filter does not work with this chart.'
} as const satisfies Record<DashboardFilterSkipReason, string>;

// Not a server skip reason: the response carried no status for this filter (a server that predates
// dashboard filters, or a response that dropped it), so there is no evidence the filter was applied.
export const UNCHECKED_FILTER_TEXT = "Couldn't check this filter for this chart.";

export const GRANULARITY_NOTE =
  'This filter switched the chart to more detailed data. Totals may differ.';

const FALLBACK_LABEL = 'Filter';

// The server's limit on top-level dashboard filters (MAX_DASHBOARD_FILTERS in bpm-lambdas getData).
export const MAX_DASHBOARD_FILTERS = 50;

const isObjectNodeList = (value: unknown): value is FilterNode[] =>
  Array.isArray(value) && value.every(isObjectNode);

// Room for MAX_RAW_FILTER_DEPTH groups (two levels each: object, list) plus a leaf's own fields.
const MAX_JSON_DEPTH = 2 * MAX_RAW_FILTER_DEPTH + 8;

// Nesting anywhere, a leaf's values included, within `limit`. A loop, so it cannot overflow itself.
const withinJsonDepth = (value: unknown, limit: number): boolean => {
  const stack: [unknown, number][] = [[value, 1]];
  for (let entry = stack.pop(); entry; entry = stack.pop()) {
    const [current, depth] = entry;
    if (typeof current === 'object' && current !== null) {
      if (depth > limit) return false;
      const children: unknown[] = Array.isArray(current) ? current : Object.values(current);
      children.forEach(child => stack.push([child, depth + 1]));
    }
  }
  return true;
};

// The server validates each node and reports a bad one on its own chip, so only the list's shape,
// size and depth are checked here. Deeper nesting would overflow the stack in recursive helpers.
export const isFilterNodeList = (value: unknown): value is FilterNode[] =>
  isObjectNodeList(value) &&
  value.length <= MAX_DASHBOARD_FILTERS &&
  withinJsonDepth(value, MAX_JSON_DEPTH) &&
  filterDepth(value, MAX_RAW_FILTER_DEPTH) <= MAX_RAW_FILTER_DEPTH;

// Only a non-empty string is a dimension id; anything else would reach the chip as a non-string.
const isDimensionId = (value: unknown): value is DimensionId =>
  typeof value === 'string' && value !== '';

// A card's opt-out list: an array of dimension ids. Empty means no opt-out.
export const isIgnoreList = (value: unknown): value is DimensionId[] =>
  Array.isArray(value) && value.every(isDimensionId);

// Saved defaults are untrusted; filterDimensions skips malformed leaves and groups.
const firstDimension = (node: FilterNode): DimensionId | undefined =>
  filterDimensions([node]).find(isDimensionId);

// A filter's display text. A malformed or empty node formats to nothing but still needs a label.
export const dashboardFilterLabel = (node: FilterNode): string => {
  const firstId = firstDimension(node);
  return formatFilterNode(node) || (firstId ? filterDimensionLabel(firstId) : FALLBACK_LABEL);
};

// What the chips need to know about the card: the chart drawn (for its own filters) and the
// dimensions the card opts out of.
interface DashboardFilterChipContext {
  chartConfig?: ChartConfig;
  ignoreDashboardFilters?: DimensionId[];
}

type SkippedStatus = Extract<DashboardFilterStatus, { status: 'skipped' }>;

// The dimensions a skip reason is about, where the card knows them: the chart's own filters, or its
// opt-outs.
const reasonDimensions = (
  reason: DashboardFilterSkipReason,
  { chartConfig, ignoreDashboardFilters }: DashboardFilterChipContext
): DimensionId[] => {
  if (reason === 'CHART_HAS_OWN_FILTER') {
    const queries = Array.isArray(chartConfig?.queries) ? chartConfig.queries : [];
    return filterDimensions(
      queries.flatMap(query => (Array.isArray(query?.filters) ? query.filters : []))
    );
  }
  if (reason === 'CARD_OPTED_OUT') return ignoreDashboardFilters ?? [];
  return [];
};

// A group's skip can come from any of its dimensions, so name the one the reason is about.
const skipDimension = (
  status: SkippedStatus,
  node: FilterNode,
  card: DashboardFilterChipContext
): DimensionId | undefined => {
  const relevant = reasonDimensions(status.reason, card);
  const dimensions = Array.isArray(status.dimensions)
    ? status.dimensions.filter(isDimensionId)
    : [];
  return dimensions.find(id => relevant.includes(id)) ?? dimensions[0] ?? firstDimension(node);
};

// The dashboard filters SENT with the response that the server confirmed applied, in dashboard order.
export const appliedDashboardFilterNodes = ({
  data,
  dashboardFilters
}: Pick<ChartWithData, 'data' | 'dashboardFilters'>): FilterNode[] => {
  const statuses = data.dashboardFilterResult?.statuses ?? [];
  return dashboardFilters.filter((_, index) =>
    statuses.some(s => s.filterIndex === index && s.status === 'applied')
  );
};

// One chip per dashboard filter SENT with the response, paired to its statuses by `filterIndex`. A
// filter with no status was not confirmed applied, so it reads as skipped.
export const buildDashboardFilterChips = (
  { config, data, dashboardFilters }: ChartWithData,
  ignoreDashboardFilters?: DimensionId[]
): DashboardFilterChip[] =>
  dashboardFilters.map((node, index): DashboardFilterChip => {
    const label = dashboardFilterLabel(node);
    const status = data.dashboardFilterResult?.statuses.find(s => s.filterIndex === index);
    if (status?.status === 'skipped') {
      const dimension = skipDimension(status, node, {
        chartConfig: config,
        ignoreDashboardFilters
      });
      return {
        label,
        applied: false,
        // A reason this client does not know yet (a newer server) gets the generic text.
        reasonText: (
          SKIP_REASON_TEXT[status.reason] ?? SKIP_REASON_TEXT.INVALID_WITH_CHART
        ).replaceAll('{dimension}', dimension ? filterDimensionLabel(dimension) : 'this')
      };
    }
    if (!status) return { label, applied: false, reasonText: UNCHECKED_FILTER_TEXT };
    return {
      label,
      applied: true,
      ...(status.granularityChanged && { granularityNote: GRANULARITY_NOTE })
    };
  });
